import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import ScrollReveal from '../components/ScrollReveal';

const STEPS = [
  {
    num: '01',
    title: 'Nettoyer',
    desc: '\u00c9liminez les impuret\u00e9s accumul\u00e9es tout au long de la journ\u00e9e. Un nettoyage en douceur pr\u00e9pare votre peau \u00e0 recevoir les actifs.',
    tip: 'Massez 60 secondes \u00e0 l\u2019eau ti\u00e8de',
    image: 'https://images.unsplash.com/photo-1685703206176-7562d4166976?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxibGFjayUyMHdvbWFuJTIwc2tpbmNhcmUlMjByb3V0aW5lJTIwZmFjZSUyMGdsb3dpbmd8ZW58MHx8fHwxNzc2Mzk1MjM5fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    num: '02',
    title: 'Traiter',
    desc: 'Appliquez des s\u00e9rums cibl\u00e9s sur peau l\u00e9g\u00e8rement humide. Les actifs concentr\u00e9s p\u00e9n\u00e8trent en profondeur pour transformer votre peau.',
    tip: '3\u20134 gouttes, pressez doucement',
    image: 'https://images.unsplash.com/photo-1648203276014-20f97ba1f817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxibGFjayUyMHdvbWFuJTIwc2tpbmNhcmUlMjByb3V0aW5lJTIwZmFjZSUyMGdsb3dpbmd8ZW58MHx8fHwxNzc2Mzk1MjM5fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    num: '03',
    title: 'Hydrater',
    desc: 'Scellez l\u2019hydratation avec une cr\u00e8me adapt\u00e9e. La barri\u00e8re cutan\u00e9e est renforc\u00e9e, votre peau reste prot\u00e9g\u00e9e toute la journ\u00e9e.',
    tip: 'Mouvements ascendants, matin & soir',
    image: 'https://images.unsplash.com/photo-1646457417455-77a66a9fcf34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwc2tpbmNhcmUlMjByb3V0aW5lJTIwZmFjZSUyMGdsb3dpbmd8ZW58MHx8fHwxNzc2Mzk1MjM5fDA&ixlib=rb-4.1.0&q=85',
  },
];

export default function RoutinePage() {
  const heroRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    if (heroRef.current) {
      gsap.fromTo(heroRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out' });
    }
    if (lineRef.current) {
      gsap.fromTo(lineRef.current, { scaleY: 0 }, { scaleY: 1, duration: 1.5, delay: 0.5, ease: 'power3.out' });
    }
  }, []);

  return (
    <div data-testid="routine-page" className="bg-[#F5F0E8]">
      {/* Hero */}
      <section className="min-h-[80vh] flex items-center justify-center pt-20 relative overflow-hidden">
        <div ref={heroRef} className="text-center px-4 max-w-3xl mx-auto relative z-10">
          <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/30 mb-8">La Routine Balmart</p>
          <p className="text-lg sm:text-xl text-black/60 leading-relaxed max-w-xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            R&eacute;v&eacute;lez l'&eacute;clat naturel de votre peau gr&acirc;ce &agrave; une routine simple et efficace.
          </p>
          <p className="mt-4 text-sm text-black/40 max-w-md mx-auto">
            Trois &eacute;tapes essentielles pour des r&eacute;sultats visibles et durables
          </p>

          {/* Animated vertical line */}
          <div ref={lineRef} className="w-px h-20 bg-black/20 mx-auto mt-12 origin-top" />
        </div>

        {/* Floating decorative circles */}
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-[#F5F5F3] opacity-50 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-[#F0F0ED] opacity-40 blur-2xl" />
      </section>

      {/* Full-width image banner */}
      <ScrollReveal>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1764662028080-227b6ccc808f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxibGFjayUyMHdvbWFuJTIwc2tpbmNhcmUlMjByb3V0aW5lJTIwZmFjZSUyMGdsb3dpbmd8ZW58MHx8fHwxNzc2Mzk1MjM5fDA&ixlib=rb-4.1.0&q=85"
              alt="Routine soin"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <p className="text-white text-xs uppercase tracking-[0.3em] font-bold">Votre Rituel</p>
              <p className="text-white/60 text-sm mt-1">Simple. Efficace. Transformateur.</p>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 3 Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <div className="space-y-32 sm:space-y-40">
          {STEPS.map((step, i) => {
            const isEven = i % 2 === 1;
            return (
              <ScrollReveal key={step.num}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                  {/* Image */}
                  <div className={`relative overflow-hidden ${isEven ? 'lg:order-2' : ''}`}>
                    <div className="aspect-[4/5] overflow-hidden group">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Step number overlay */}
                    <div className="absolute top-6 left-6">
                      <span className="text-8xl font-serif font-light text-white/20" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {step.num}
                      </span>
                    </div>
                  </div>

                  {/* Text */}
                  <div className={`${isEven ? 'lg:order-1' : ''} flex flex-col justify-center`}>
                    <div className="mb-6">
                      <span className="text-[10rem] sm:text-[12rem] font-serif font-light text-black/[0.03] leading-none block -mb-16 sm:-mb-20" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {step.num}
                      </span>
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {step.title}
                    </h2>
                    <p className="text-base text-black/60 leading-relaxed mb-8 max-w-md">
                      {step.desc}
                    </p>
                    <div className="flex items-center gap-4 border-l-2 border-black pl-6">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/30 mb-1">Conseil</p>
                        <p className="text-sm text-black/50">{step.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA with image grid */}
      <section className="bg-[#EDE8DE] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-16">
              <div className="aspect-square overflow-hidden">
                <img src="https://images.unsplash.com/photo-1611080541599-8c6dbde6ed28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxza2luY2FyZSUyMGJvdHRsZSUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYzNzQ2ODF8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden">
                <img src="https://images.unsplash.com/photo-1732901379140-eea439bded04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxza2luY2FyZSUyMHNlcnVtJTIwZHJvcHBlciUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYzNzQ2ODF8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden">
                <img src="https://images.unsplash.com/photo-1770048792338-aaf6a575305f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxza2luY2FyZSUyMGphciUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYzNzQ2ODF8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden">
                <img src="https://images.unsplash.com/photo-1665763630810-e6251bdd392d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxza2luY2FyZSUyMGJvdHRsZSUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYzNzQ2ODF8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="text-center">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Pr&ecirc;te &agrave; commencer ?
              </h2>
              <p className="text-base text-black/50 max-w-md mx-auto mb-10">
                D&eacute;couvrez nos produits s&eacute;lectionn&eacute;s pour chaque &eacute;tape de votre routine.
              </p>
              <Link to="/shop" data-testid="routine-shop-btn" className="btn-primary inline-block">
                D&eacute;couvrir les Produits
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
